<template>
  <Card class="px-1 w-full my-auto" pt:content:class="p-0!">
    <template #content>
      <div class="flex flex-col gap-3 items-center">
        <InputGroup class="w-[500px]">
          <Select
            v-model="selectedBudgetItem"
            :options="budgetItems"
            optionLabel="description"
            placeholder="Select a Budget Item"
            class="w-96 h-10"
            scroll-height="30rem"
            @change="budgetItemChanged"
          >
            <template #value="slotProps">
              <div v-if="slotProps.value" class="flex items-center">
                <div>{{ slotProps.value.description }}</div>
                <Tag severity="secondary" class="ml-2 text-xs">{{ slotProps.value.costType }}</Tag>
                <Tag severity="info" class="ml-2 text-xs">{{ slotProps.value.custCostType }}</Tag>
              </div>
              <span v-else>
                {{ slotProps.placeholder }}
              </span>
            </template>
            <template #option="slotProps">
              <div class="flex items-center">
                <div>{{ slotProps.option.description }}</div>
                <Tag severity="secondary" class="ml-2 text-xs">{{ slotProps.option.costType }}</Tag>
                <Tag severity="info" class="ml-2 text-xs">{{ slotProps.option.custCostType }}</Tag>
              </div>
            </template>
          </Select>
          <IconButton
            v-tooltip.top="'Delete budget item'"
            icon="trash"
            severity="danger"
            :disabled="!deletable"
            outlined
            @click="tryDeleteBudgetItem"
          />
          <IconButton v-tooltip.top="'Create new budget item'" icon="plus" severity="success" outlined @click="adding = true" />
        </InputGroup>

        <InputGroup class="w-fit">
          <Select
            v-model="selectedBudgetColumn"
            :options="budgetColumns"
            optionLabel="label"
            placeholder="Select a Budget Column"
            class="w-96 h-10"
            scroll-height="30rem"
            :disabled="readOnly"
          />
          <IconButton
            v-tooltip.top="'Add column to reports'"
            icon="check-circle"
            severity="success"
            outlined
            :disabled="readOnly"
            @click="addReportColumn"
          />
        </InputGroup>

        <OrderList
          v-model="reportColumns"
          class="w-[500px] h-[50vh] mr-15"
          pt:pcListbox:root="w-full h-full"
          pt:pcListbox:listcontainer="w-full h-full max-h-none!"
          :disabled="readOnly"
        >
          <template #option="{ option }">
            <div class="flex flex-row w-full justify-between items-center">
              <p>{{ option.label }}</p>
              <IconButton
                icon="pi pi-times"
                class="rounded-full"
                small
                outlinedb
                severity="danger"
                :disabled="readOnly"
                @click="removeColumn(option)"
              />
            </div>
          </template>
        </OrderList>
        <SubmitButton class="w-96" label="Save Budget Item" :disabled="readOnly || !selectedBudgetItem" @click="updateBudgetItem" />
      </div>
    </template>
  </Card>
  <TSDialog v-model:visible="adding" label="Add Budget Item" modal size="w-[750px]!" icon="plus">
    <template #content>
      <CreateCustomBudgetItemModal :custCostTypes @create-item="createBudgetItem" />
    </template>
  </TSDialog>
</template>

<script setup lang="ts">
import { BudgetColumns } from "@/enums/BudgetColumns";
import { confirmPopup, getCodeTable, packBit, parseData2, testBit } from "@/services/Utility";
import type { CustomizableColumn } from "@/types/CustomizableColumns";
import { StockBudgetItemListSchema, type StockBudgetItem, StockBudgetItemSchema } from "@/types/StockBudgetItem";
import { CodeTableNumber } from "@/enums/CodeTable";
import type { Code } from "@/types/Codes";
import { useConfirm } from "primevue/useconfirm";

const selectedBudgetItem = ref<StockBudgetItem>();
const budgetItems = ref<StockBudgetItem[]>([]);

const selectedBudgetColumn = ref();
const allColumns = ref<CustomizableColumn[]>([]);
const budgetColumns = ref<CustomizableColumn[]>([]);
const reportColumns = ref<CustomizableColumn[]>([]);

const custCostTypes = ref<Code[]>([]);

const readOnly = ref<boolean>(true);
const deletable = ref<boolean>(false);
const adding = ref<boolean>(false);

const confirm = useConfirm();

const unavailableColumns: string[] = [
  BudgetColumns.delete.label,
  BudgetColumns.history.label,
  BudgetColumns.cutChecks.label,
  BudgetColumns.percentBudget.label,
];

const emit = defineEmits<{ itemsUpdated: [items: StockBudgetItem[]] }>();

const standardBudgetItems: string[] = ["**INTEREST PAYMENT", "**INTEREST RESERVE", "DRAW BASED RETAINAGE"];

const sortBudgetItems = (): void => {
  budgetItems.value.sort((a, b) => a.description.localeCompare(b.description));
};

const budgetItemChanged = (): void => {
  readOnly.value = selectedBudgetItem.value == null;
  deletable.value = selectedBudgetItem.value != null && !standardBudgetItems.includes(selectedBudgetItem.value.description);
  reportColumns.value = [];
  budgetColumns.value = [];

  Object.entries(allColumns.value).forEach((col: any) => {
    if (selectedBudgetItem.value?.columns != null && testBit(selectedBudgetItem.value.columns, col[1].bit)) reportColumns.value.push(col[1]);
    else budgetColumns.value.push(col[1]);
  });
};

const addReportColumn = (): void => {
  if (selectedBudgetColumn.value != null) {
    reportColumns.value.push(selectedBudgetColumn.value);
    budgetColumns.value = budgetColumns.value.filter((col) => col.bit !== selectedBudgetColumn.value.bit);
    selectedBudgetColumn.value = null;
  }
};

const removeColumn = (column: CustomizableColumn): void => {
  if (!deletable.value) return;

  reportColumns.value = reportColumns.value.filter((c) => c.bit !== column.bit);

  const alreadyInBudget = budgetColumns.value.some((c) => c.bit === column.bit);
  if (!alreadyInBudget) budgetColumns.value.push(column);

  budgetColumns.value.sort((a, b) => a.label.localeCompare(b.label));
  if (selectedBudgetColumn.value?.bit === column.bit) selectedBudgetColumn.value = null;
};

const createBudgetItem = (item: StockBudgetItem): void => {
  axios
    .post("/stock-budget-items/add", item)
    .then((response) =>
      handleApiResponse(
        response,
        (result: any) => {
          const newItem = parseData2(StockBudgetItemSchema, result);
          budgetItems.value.push(newItem);
          sortBudgetItems();
          selectedBudgetItem.value = newItem;
          budgetItemChanged();
          adding.value = false;
          emit("itemsUpdated", budgetItems.value);
          success("New Budget Item Added", "");
        },
        (errors: ApiError[]) => {
          error("Could not update custom budget item", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));
};

const updateBudgetItem = (): void => {
  if (!selectedBudgetItem.value) return;
  if (readOnly.value) return;

  // let columns = 0;
  // reportColumns.value.forEach((col) => {
  //   columns = packBit(columns, true, col.bit);
  // });

  const item: StockBudgetItem = {
    ...selectedBudgetItem.value,
    columns: reportColumns.value.reduce((columns, col) => packBit(columns, true, col.bit), 0),
    columnOrder: reportColumns.value.map((c) => c.bit),
  };

  axios
    .post("/stock-budget-items/update", item)
    .then((response) =>
      handleApiResponse(
        response,
        () => {
          if (selectedBudgetItem.value && selectedBudgetItem.value.stockBudgetItemId == item.stockBudgetItemId) {
            selectedBudgetItem.value.columns = item.columns;
            selectedBudgetItem.value.columnOrder = item.columnOrder;
          }
          const idx = budgetItems.value.findIndex((b) => b.stockBudgetItemId === item.stockBudgetItemId);
          if (idx !== -1) budgetItems.value[idx] = item;

          emit("itemsUpdated", budgetItems.value);
          success("Success", "Budget item updated");
        },
        (errors: ApiError[]) => {
          error("Could not update custom budget item", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));
};

const tryDeleteBudgetItem = (): void => {
  confirmPopup(confirm, "Delete Budget Item", "Are you sure you want to delete this budget item", deleteBudgetItem, () =>
    info("Delete cancelled", "")
  );
};

const deleteBudgetItem = (): void => {
  const id: string = selectedBudgetItem.value?.stockBudgetItemId ?? "";
  axios
    .post("/stock-budget-items/delete/" + id)
    .then((response) =>
      handleApiResponse(
        response,
        () => {
          budgetItems.value = budgetItems.value.filter((item) => item.stockBudgetItemId !== id);
          selectedBudgetItem.value = undefined;
          budgetItemChanged();
          emit("itemsUpdated", budgetItems.value);
          success("Success", "Budget item deleted");
        },
        (errors: ApiError[]) => {
          error("Could not delete custom budget item", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));
};

onMounted(async (): Promise<void> => {
  allColumns.value = Object.entries(BudgetColumns)
    .filter((col) => {
      return !unavailableColumns.includes(col[1].label);
    })
    .sort((a, b) => a[1].label.localeCompare(b[1].label))
    .map((col) => col[1]);

  budgetItemChanged();
  axios
    .get("/stock-budget-items")
    .then((response) => {
      handleApiResponse(
        response,
        (result: any) => {
          parseData(StockBudgetItemListSchema, result, budgetItems);
        },
        (errors: ApiError[]) => {
          error("Could not retrieve customizable budget items", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));

  getCodeTable(CodeTableNumber.CUSTOM_COST_TYPES).then((result: Code[]) => {
    custCostTypes.value = result;
  });
});
</script>
